import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { motion } from 'framer-motion';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ChatProvider } from './contexts/ChatContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { specialThemesManager } from './utils/specialThemes';
import './styles/specialThemes.css';
import ModernLoginPage from './components/ModernLoginPage';
import ModernRegisterPage from './components/ModernRegisterPage';
import ModernDashboard from './components/ModernDashboard';
import FeedbackModal from './components/FeedbackModal';
import AdminPanel from './components/AdminPanel';
import LoadingSpinner from './components/LoadingSpinner';
import Login from './components/Login';
import ModernChatInterface from './components/ModernChatInterface';
import ModernProfile from './components/ModernProfile';
import Premium from './components/Premium';
import PaymentTest from './components/PaymentTest';
import AdminTest from './components/AdminTest';
import DatabaseManagement from './components/DatabaseManagement';
import './App.css';

function AppContent() {
  const { user, loading, login, loginWithEmail } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [showLogin, setShowLogin] = useState(false);
  const [showRegister, setShowRegister] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [specialDay, setSpecialDay] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);

  useEffect(() => {
    if (!loading) {
      setIsLoading(false);
    }
  }, [loading]);

  // Responsive breakpoint kontrolü
  useEffect(() => {
    const checkDevice = () => {
      const width = window.innerWidth;
      setIsMobile(width < 768);
      setIsTablet(width >= 768 && width < 1024);
    };

    checkDevice();
    window.addEventListener('resize', checkDevice);
    return () => window.removeEventListener('resize', checkDevice);
  }, []);

  // Özel gün kontrolü
  useEffect(() => {
    const checkSpecialDay = () => {
      const todaySpecial = specialThemesManager.getTodaySpecialDay();
      setSpecialDay(todaySpecial);
      
      if (todaySpecial) {
        specialThemesManager.applySpecialTheme(todaySpecial);
        document.body.classList.add('special-theme-active');
      } else {
        document.body.classList.remove('special-theme-active');
      }
    };

    checkSpecialDay();
    
    // Her gün kontrol et (gece yarısı)
    const now = new Date();
    const tomorrow = new Date(now);
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(0, 0, 0, 0);
    
    const timeUntilMidnight = tomorrow.getTime() - now.getTime();
    const timeoutId = setTimeout(checkSpecialDay, timeUntilMidnight);
    
    return () => clearTimeout(timeoutId);
  }, []);

  if (isLoading) {
    return <LoadingSpinner />;
  }

  // Show login/register if no user
  if (!user) {
    if (showRegister) {
      return (
        <ModernRegisterPage 
          onRegister={() => setShowRegister(false)}
          onSwitchToLogin={() => setShowRegister(false)}
        />
      );
    }
    return (
      <ModernLoginPage 
        onLogin={(user) => {
          console.log('Login successful, user:', user);
          login(user);
          setShowLogin(false);
        }}
        onSwitchToRegister={() => setShowRegister(true)}
        loginWithEmail={loginWithEmail}
      />
    );
  }

  return (
    <Router>
      <div className="App relative gokturk-theme gokturk-bg h-screen overflow-hidden">
        {/* Göktürk Header */}
        <div className={`fixed top-0 left-0 right-0 z-40 text-white text-center shadow-lg gokturk-header ${
          specialDay ? 'special-theme-active' : ''
        } ${isMobile ? 'py-1 px-2' : 'py-0.5 px-1'}`}>
          <motion.div
            className={`font-bold leading-none ${
              isMobile 
                ? 'text-[8px] xs:text-[9px]' 
                : isTablet 
                  ? 'text-[10px] sm:text-xs' 
                  : 'text-xs md:text-sm lg:text-base xl:text-lg'
            }`}
            animate={{ 
              textShadow: specialDay ? [
                "0 0 8px rgba(255,255,255,0.8)",
                "0 0 16px rgba(255,255,255,1)",
                "0 0 8px rgba(255,255,255,0.8)"
              ] : [
                "0 0 6px rgba(255,255,255,0.7)",
                "0 0 12px rgba(255,255,255,1)",
                "0 0 6px rgba(255,255,255,0.7)"
              ]
            }}
            transition={{ duration: 2, repeat: Infinity }}
          >
            <span className={`flex items-center justify-center gokturk-title ${isMobile ? 'gap-1' : 'gap-2'}`}>
              {specialDay && <span className={isMobile ? 'text-sm' : 'text-lg'}>🐺</span>}
              <span>🐺 GÖKTÜRKGPT - TÜRK YAPAY ZEKASI 🐺</span>
            </span>
          </motion.div>
        </div>

        {/* Göktürk Feedback Button */}
        <button
          onClick={() => setShowFeedback(true)}
          className={`fixed z-30 gokturk-btn rounded-full shadow-lg transition-all duration-200 hover:scale-110 ${
            isMobile 
              ? 'bottom-3 right-3 p-2' 
              : 'bottom-4 right-4 p-3'
          }`}
          title="🐺 İstek ve Öneri Bildirimi"
        >
          <svg className={isMobile ? 'w-5 h-5' : 'w-6 h-6'} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
          </svg>
        </button>

        <Toaster 
          position="top-right"
          toastOptions={{
            duration: 4000,
            style: {
              background: '#363636',
              color: '#fff',
            },
          }}
        />
        
        {/* Feedback Modal */}
        <FeedbackModal 
          isOpen={showFeedback} 
          onClose={() => setShowFeedback(false)} 
        />
        
        <Routes>
          <Route 
            path="/login" 
            element={user ? <Navigate to="/" /> : <Login />} 
          />
          <Route 
            path="/" 
            element={user ? <ModernDashboard /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/chat/:chatId" 
            element={user ? <ModernChatInterface /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/profile" 
            element={user ? <ModernProfile /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/admin" 
            element={user?.is_admin ? <AdminPanel /> : <Navigate to="/" />} 
          />
          <Route 
            path="/premium" 
            element={user ? <Premium /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/payment-test" 
            element={<PaymentTest />} 
          />
          <Route 
            path="/admin-test" 
            element={<AdminTest />} 
          />
          <Route 
            path="/database" 
            element={user?.is_admin ? <DatabaseManagement /> : <Navigate to="/login" />} 
          />
        </Routes>
      </div>
    </Router>
  );
}

function App() {
  return (
    <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID || "your-google-client-id"}>
      <ThemeProvider>
        <AuthProvider>
          <ChatProvider>
            <AppContent />
          </ChatProvider>
        </AuthProvider>
      </ThemeProvider>
    </GoogleOAuthProvider>
  );
}

export default App;
